import { SIXTEENTHS, type EdgeViewRecord, type EntityState } from "odyssey-shared";
import type { EdgeNearFrame } from "../handoff-wire.ts";
import { entityKey } from "./reach.ts";

/**
 * A map of the world manifest as the book reads it: its id and, for an outdoor map, its place in whole tiles.
 */
export interface BookMap {
  readonly id: string;
  readonly place?: { readonly x: number; readonly y: number; readonly depth?: number } | undefined;
}

/**
 * The page's own character's name, which a neighbour's edge view carries between the neighbour's join and the welcome.
 */
export interface OwnName {
  readonly first: string;
  readonly family: string;
}

/**
 * One neighbour's entities as the book holds them.
 */
interface Held {
  tick: number;
  entities: Map<number, EntityState>;
  next: Map<number, EntityState>;
}

/**
 * The edge views the page holds (ADR-147 decisions 7, 13 and 26). Each neighbour's entities are filed under
 * `entityKey(mapIndex, handle)`, the index their map's place in the manifest's `maps`, the same table the map server
 * encodes it from, so two maps' equal handles never meet; each entity is moved into the owning map's frame by the two
 * places (decision 6), so one west or north of the owner stands at negative coordinates, and carries its key as its
 * handle, so a collection drawing it files it under the key. A view is read only from a neighbour the gateway said is near
 * (`edgeNear`) on the world version the page holds (decision 26); a neighbour gone far or onto another version is dropped.
 * An entity a view no longer carries is reported vanished, once, so its figure is held as a `crossed` one is (ADR-147
 * note 8 item a: an edge view is a keyframe and carries no removal list). The page's own character is left out.
 */
export class EdgeBook {
  private readonly held = new Map<number, Held>();
  private readonly versions = new Map<string, string>();
  private readonly near_ = new Set<string>();
  private owner = -1;
  private readonly empty: ReadonlyMap<number, EntityState> = new Map();

  /**
   * @param maps The world manifest's maps, in its order.
   * @param version The world version the page holds.
   * @param vanished Told the key of each entity a view stopped carrying.
   */
  public constructor(
    private readonly maps: readonly BookMap[],
    private readonly version: string,
    private readonly vanished: (key: number) => void,
  ) {}

  /**
   * Stands the book on a map: the entities held are dropped without being reported, since the caller hands each figure on
   * as the origin moves, and every neighbour's later view is moved into this map's frame. What each neighbour runs and
   * whether it is near are kept.
   *
   * @param mapId The owning map's id.
   */
  public stand(mapId: string): void {
    this.owner = this.maps.findIndex((map) => map.id === mapId);
    this.held.clear();
  }

  /**
   * Takes an `edgeNear`: a neighbour whose view begins or ends, and the version it runs. A neighbour no longer drawable
   * has its entities reported vanished and dropped.
   *
   * @param frame The frame.
   */
  public near(frame: EdgeNearFrame): void {
    this.versions.set(frame.map, frame.version);
    if (frame.near) {
      this.near_.add(frame.map);
    } else {
      this.near_.delete(frame.map);
    }
    if (!this.drawable(frame.map)) {
      this.drop(this.maps.findIndex((map) => map.id === frame.map));
    }
  }

  /**
   * Whether a neighbour's entities are drawn: the gateway said it is near, on the version the page holds.
   *
   * @param mapId The neighbour's id.
   * @returns True when they are.
   */
  public drawable(mapId: string): boolean {
    return this.near_.has(mapId) && this.versions.get(mapId) === this.version;
  }

  /**
   * The version a neighbour runs, as its last `edgeNear` named it.
   *
   * @param mapId The neighbour's id.
   * @returns The version, or null before any `edgeNear` named the map.
   */
  public versionOf(mapId: string): string | null {
    return this.versions.get(mapId) ?? null;
  }

  /**
   * Takes one edge view. A view from the owning map, from a map with no place, from an index past the manifest, or from a
   * neighbour not drawable is ignored.
   *
   * @param view The decoded view, valid only during the call.
   * @param own The page's own character's name, left out of the view, or null before a welcome.
   * @returns True when the view was taken.
   */
  public receive(view: Readonly<EdgeViewRecord>, own: OwnName | null): boolean {
    const index = view.map;
    const map = this.maps[index];
    const owner = this.maps[this.owner];
    if (index === this.owner || map?.place === undefined || owner?.place === undefined || !this.drawable(map.id)) {
      return false;
    }
    const dx = (map.place.x - owner.place.x) * SIXTEENTHS;
    const dy = (map.place.y - owner.place.y) * SIXTEENTHS;
    let held = this.held.get(index);
    if (held === undefined) {
      held = { tick: view.tick, entities: new Map(), next: new Map() };
      this.held.set(index, held);
    }
    const next = held.next;
    next.clear();
    for (const entity of view.entities) {
      const state = entity.state;
      if (own !== null && state.name.first === own.first && state.name.family === own.family) {
        continue;
      }
      const key = entityKey(index, entity.handle);
      next.set(key, { ...state, handle: key, x: state.x + dx, y: state.y + dy });
    }
    for (const key of held.entities.keys()) {
      if (!next.has(key)) {
        this.vanished(key);
      }
    }
    held.next = held.entities;
    held.entities = next;
    held.tick = view.tick;
    return true;
  }

  /**
   * A neighbour's entities, keyed by `entityKey`, in the owning map's frame.
   *
   * @param index The neighbour's manifest index.
   * @returns The entities; empty for a map the book holds nothing of.
   */
  public entities(index: number): ReadonlyMap<number, EntityState> {
    return this.held.get(index)?.entities ?? this.empty;
  }

  /**
   * The tick of a neighbour's last view, in that neighbour's own count.
   *
   * @param index The neighbour's manifest index.
   * @returns The tick, or -1 for a map the book holds nothing of.
   */
  public tickOf(index: number): number {
    return this.held.get(index)?.tick ?? -1;
  }

  /**
   * The manifest indices of the neighbours the book holds a view of, in order.
   *
   * @returns The indices.
   */
  public indices(): number[] {
    return [...this.held.keys()].sort((a, b) => a - b);
  }

  /**
   * Drops everything held, reporting nothing, as a session ends.
   */
  public clear(): void {
    this.held.clear();
    this.near_.clear();
    this.versions.clear();
  }

  /**
   * Reports every entity of a neighbour vanished and drops them.
   *
   * @param index The neighbour's manifest index.
   */
  private drop(index: number): void {
    const held = this.held.get(index);
    if (held === undefined) {
      return;
    }
    for (const key of held.entities.keys()) {
      this.vanished(key);
    }
    this.held.delete(index);
  }
}
